import PublicLayout from "@/components/layout/PublicLayout";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
    Home as HomeIcon,
    Building2,
    Layers,
    ScrollText,
    Clock,
    ShieldCheck,
    Eye,
    ArrowUpRight,
} from "lucide-react";
import InvestorDashboardPreview from "@/components/investor/preview/InvestorDashboardPreview";
import InvestorComparePreview, {
    COMPARE_COMBOS,
    CompareTiersButton,
} from "@/components/investor/preview/InvestorComparePreview";
import InvestorPlansComparisonTable from "@/components/investor/InvestorPlansComparisonTable";
import InvestorValueCalculator from "@/components/investor/InvestorValueCalculator";
import { track } from "@/lib/analytics";

function PersonaPage({
    title,
    subtitle,
    intro,
    bullets,
    quote,
    cta,
    ctaLabel,
    pageTestid,
    sideCard,
    afterMain,
}) {
    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container pt-16 pb-10" data-testid={pageTestid}>
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        <div className="lg:col-span-7 min-w-0" data-reveal="slide-left-lg">
                            <div className="bl-eyebrow">{subtitle}</div>
                            <h1 className="mt-5 font-display font-semibold text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[1.02] text-bl max-w-3xl">
                                {title}
                            </h1>
                            <p className="mt-6 text-bl-muted text-lg max-w-2xl leading-relaxed">
                                {intro}
                            </p>
                            <div className="mt-7 flex flex-wrap items-center gap-3">
                                <Link
                                    to={cta}
                                    className="bl-btn-primary"
                                    data-testid="persona-cta-top"
                                >
                                    {ctaLabel}
                                </Link>
                                <Link to="/pricing" className="bl-btn-secondary">
                                    See pricing
                                </Link>
                            </div>
                        </div>

                        {sideCard && (
                            <div
                                className="lg:col-span-5 min-w-0"
                                data-reveal="slide-right-lg"
                                data-reveal-delay="2"
                            >
                                {sideCard}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <section className="bl-container py-12">
                <div className="grid md:grid-cols-2 gap-5">
                    {bullets.map(([t, d], i) => (
                        <div
                            key={t}
                            className="bl-card p-7"
                            data-reveal="rise-sm"
                            data-reveal-delay={Math.min(5, i + 1)}
                        >
                            <h3 className="font-display font-semibold text-xl text-bl">
                                {t}
                            </h3>
                            <p className="mt-3 text-bl-muted leading-relaxed">{d}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-12 bl-pullquote max-w-3xl" data-reveal="rise-sm">
                    {quote}
                </div>

                <div className="mt-12 flex gap-3 flex-wrap">
                    <Link to={cta} className="bl-btn-primary" data-testid="persona-cta">
                        {ctaLabel}
                    </Link>
                    <Link to="/pricing" className="bl-btn-secondary">
                        See pricing
                    </Link>
                </div>
            </section>
            {afterMain}
        </PublicLayout>
    );
}

function BuyerSideCard() {
    return (
        <div className="bl-card p-6 md:p-7" data-testid="for-buyers-side">
            <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                    <HomeIcon className="w-3.5 h-3.5" />
                    Built for buyers
                </div>
                <span className="font-mono-bl text-xs text-bl-muted">
                    1-time · From $49
                </span>
            </div>

            <h3 className="mt-4 font-display text-xl font-semibold text-bl">
                A calmer way to negotiate.
            </h3>
            <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                Upload your inspection PDF once. Get a prioritized, plain-English
                breakdown of what to negotiate, what to live with, and what to
                walk away from.
            </p>

            <div className="mt-5 space-y-2">
                {[
                    [ScrollText, "Plain-English issue summaries"],
                    [Layers, "Negotiation strategy framing"],
                    [Clock, "Ready in minutes"],
                    [ShieldCheck, "Private to your account"],
                ].map(([Ico, label], i) => (
                    <div
                        key={label}
                        className="flex items-center gap-3 text-sm text-bl"
                        data-reveal="rise-sm"
                        data-reveal-delay={Math.min(5, i + 1)}
                    >
                        <Ico className="w-4 h-4 text-bl-primary shrink-0" />
                        <span>{label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function InvestorSideCard() {
    return (
        <div className="bl-card p-6 md:p-7" data-testid="for-investors-side">
            <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                    <Building2 className="w-3.5 h-3.5" />
                    Built for volume
                </div>
                <span className="font-mono-bl text-xs text-bl-muted">
                    Subscriptions · From $29/mo
                </span>
            </div>

            <h3 className="mt-4 font-display text-xl font-semibold text-bl">
                Three tiers for different rhythms.
            </h3>

            <div className="mt-5 space-y-3">
                {[
                    { name: "Investor Lite", price: "$29", desc: "Casual underwriting · 3 reports/mo" },
                    { name: "Investor Pro", price: "$79", desc: "Steady deal flow · 10 reports/mo" },
                    { name: "Investor Plus", price: "$149", desc: "High-volume teams · 25 reports/mo" },
                ].map((tier, i) => (
                    <div
                        key={tier.name}
                        className="flex items-baseline justify-between rounded-lg border border-bl px-4 py-3"
                        data-reveal="rise-sm"
                        data-reveal-delay={Math.min(5, i + 1)}
                    >
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-bl">
                                {tier.name}
                            </div>
                            <div className="text-xs text-bl-muted mt-0.5">
                                {tier.desc}
                            </div>
                        </div>
                        <div className="font-display text-lg font-semibold text-bl shrink-0 ml-3">
                            {tier.price}
                            <span className="text-xs text-bl-muted font-normal">/mo</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-4 text-xs text-bl-muted">
                Cancel anytime · Unused reports don't roll over
            </div>
        </div>
    );
}

export function ForBuyers() {
    return (
        <PersonaPage
            pageTestid="for-buyers-page"
            subtitle="For homebuyers"
            title={
                <>
                    Show up to the negotiation{" "}
                    <span className="font-italic-accent text-bl-primary">calmer.</span>
                </>
            }
            intro="Most buyers experience inspection day as a wall of jargon. Buyer's Leverage gives you a calm, organized view of the home — and a clear path to a more confident negotiation."
            bullets={[
                ["Less overwhelm", "Findings are organized, simplified, and prioritized so you can think clearly about what matters."],
                ["Strategic clarity", "Closing Strategy surfaces the highest-leverage items with suggested asks."],
                ["Reduced uncertainty", "Categorized issues with severity, timeline, and recommended next steps."],
                ["Saved to your dashboard", "Reopen, export, and revisit your report anytime."],
            ]}
            quote="“I understand this inspection far better now.”"
            cta="/upload"
            ctaLabel="Upload Inspection"
            sideCard={<BuyerSideCard />}
        />
    );
}

function InvestorPreviewTrio() {
    // Sample-dashboard preview trio shown on /for-investors. Mirrors the
    // buttons on /pricing so prospects can explore the experience before
    // entering the pricing flow.
    const [previewTier, setPreviewTier] = useState(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const t = params.get("preview");
            return ["lite", "pro", "plus"].includes(t) ? t : null;
        } catch {
            return null;
        }
    });
    const [compareCombo, setCompareCombo] = useState(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const c = params.get("compare");
            return COMPARE_COMBOS[c] ? c : null;
        } catch {
            return null;
        }
    });

    useEffect(() => {
        try {
            const url = new URL(window.location.href);
            if (previewTier) url.searchParams.set("preview", previewTier);
            else url.searchParams.delete("preview");
            if (compareCombo) url.searchParams.set("compare", compareCombo);
            else url.searchParams.delete("compare");
            window.history.replaceState({}, "", url.toString());
        } catch { /* non-critical */ }
    }, [previewTier, compareCombo]);

    const open = (tier) => {
        track("investor_preview_button_clicked", {
            tier,
            source: "for_investors_page",
        });
        setPreviewTier(tier);
    };

    const openCompare = (combo) => {
        track("investor_compare_button_clicked", {
            combo,
            source: "for_investors_page",
        });
        setCompareCombo(combo);
    };

    const TIERS = [
        {
            tier: "lite",
            name: "Investor Lite",
            price: "$29/mo",
            blurb: "For occasional deal review. Standard reports + repair exposure.",
            highlight: false,
        },
        {
            tier: "pro",
            name: "Investor Pro",
            price: "$79/mo",
            blurb:
                "For active investors. Premium reports, deal comparison, negotiation guidance.",
            highlight: true,
        },
        {
            tier: "plus",
            name: "Investor Plus",
            price: "$149/mo",
            blurb:
                "For portfolio scale. Heatmaps, rankings, bulk uploads, capital planning.",
            highlight: false,
        },
    ];

    return (
        <section
            className="bl-container pb-20"
            data-testid="for-investors-preview-trio"
        >
            <div className="bl-eyebrow">See the dashboard before you subscribe</div>
            <h2 className="mt-3 font-display font-semibold text-2xl sm:text-3xl md:text-4xl tracking-tight text-bl max-w-3xl">
                Explore each tier with realistic{" "}
                <span className="font-italic-accent text-bl-primary">
                    sample data
                </span>
                .
            </h2>
            <p className="mt-3 text-bl-muted max-w-2xl">
                No signup. No payment. Click into any tier to see exactly what
                your portfolio dashboard would look like — properties, exposure,
                negotiation insight, analytics.
            </p>

            <div className="mt-8 grid md:grid-cols-3 gap-4 sm:gap-5">
                {TIERS.map((t) => (
                    <div
                        key={t.tier}
                        className={
                            "bl-card p-5 sm:p-6 flex flex-col gap-3 relative " +
                            (t.highlight
                                ? "border-bl-primary ring-1 ring-bl-primary/15"
                                : "")
                        }
                        data-testid={`for-investors-preview-card-${t.tier}`}
                    >
                        {t.highlight && (
                            <div className="absolute -top-3 left-5 inline-flex items-center px-2 py-0.5 rounded-full bg-bl-primary text-bl-surface text-[10px] tracking-[0.16em] uppercase font-medium">
                                Most Popular
                            </div>
                        )}
                        <div>
                            <div className="font-display font-semibold text-lg text-bl">
                                {t.name}
                            </div>
                            <div className="text-xs font-mono-bl text-bl-muted">
                                {t.price}
                            </div>
                        </div>
                        <p className="text-sm text-bl-muted leading-relaxed flex-1">
                            {t.blurb}
                        </p>
                        <button
                            type="button"
                            onClick={() => open(t.tier)}
                            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-medium text-bl-primary bg-bl-teal-tint hover:bg-bl-teal-tint/70 border border-bl-primary/15 transition-colors min-h-[44px]"
                            data-testid={`for-investors-preview-${t.tier}-btn`}
                        >
                            <Eye className="w-4 h-4" />
                            View Sample {t.name} Dashboard
                        </button>
                        <Link
                            to={`/pricing#investor-plans`}
                            className="inline-flex items-center justify-center gap-1.5 text-xs text-bl-muted hover:text-bl-primary"
                            data-testid={`for-investors-preview-${t.tier}-pricing`}
                        >
                            See full plan details
                            <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    </div>
                ))}
            </div>
            <div className="mt-7 sm:mt-8 flex flex-wrap items-center gap-3">
                <CompareTiersButton
                    onClick={() => openCompare("lite-vs-pro")}
                    label="Compare two tiers side-by-side"
                    testid="for-investors-compare-tiers-btn"
                />
                <span className="text-xs text-bl-muted">
                    Feature checklist comparison — see exactly what's
                    included where.
                </span>
            </div>

            <InvestorValueCalculator
                onPreview={(tier) => {
                    track("investor_preview_button_clicked", {
                        tier,
                        source: "value_calculator",
                    });
                    setPreviewTier(tier);
                }}
            />

            <div
                className="mt-12 md:mt-16"
                data-testid="for-investors-plans-table-wrap"
            >
                <div className="bl-eyebrow">Full plan comparison</div>
                <h3 className="mt-3 font-display font-semibold text-2xl sm:text-3xl tracking-tight text-bl">
                    Every feature, every tier — at a glance.
                </h3>
                <p className="mt-3 text-bl-muted max-w-2xl text-sm sm:text-base">
                    The complete feature matrix for Investor Lite, Pro, and
                    Plus. Cancel or switch tiers anytime.
                </p>
                <div className="mt-7">
                    <InvestorPlansComparisonTable testid="for-investors-comparison-table" />
                </div>
            </div>

            <InvestorDashboardPreview
                tier={previewTier || "pro"}
                open={!!previewTier}
                onOpenChange={(o) => {
                    if (!o) setPreviewTier(null);
                }}
                source="for_investors"
                onCompareTiers={(combo) => setCompareCombo(combo)}
            />
            <InvestorComparePreview
                combo={compareCombo || "lite-vs-pro"}
                open={!!compareCombo}
                onOpenChange={(o) => {
                    if (!o) setCompareCombo(null);
                }}
                source="for_investors"
            />
        </section>
    );
}

export function ForInvestors() {
    const investorHero = (
        <div className="space-y-5">
            <div
                className="overflow-hidden rounded-3xl border border-bl shadow-sm bg-bl-cream"
                data-testid="for-investors-hero-image-wrap"
            >
                <picture>
                    <source srcSet="/investors-hero-claymation.webp" type="image/webp" />
                    <img
                        src="/investors-hero-claymation.png"
                        alt="Claymation scene of a real-estate investor reviewing three clay house models with severity tags, a repair-exposure spreadsheet, and Lite, Pro, Plus folder tabs."
                        loading="eager"
                        fetchPriority="high"
                        className="w-full h-auto block"
                        data-testid="for-investors-hero-image"
                    />
                </picture>
            </div>
            <InvestorSideCard />
        </div>
    );
    return (
        <PersonaPage
            pageTestid="for-investors-page"
            subtitle="For investors"
            title={
                <>
                    Faster signal on{" "}
                    <span className="font-italic-accent text-bl-primary">
                        repair exposure
                    </span>{" "}
                    and deal quality.
                </>
            }
            intro="When you analyze properties at volume, inspection reports become operational drag. Buyer's Leverage organizes the findings into a consistent, scannable view of repair exposure and risk."
            bullets={[
                ["Consistent structure", "Every report follows the same prioritized format — easier to compare and underwrite."],
                ["Repair exposure ranges", "Broad planning ranges for budgeting, not contractor bids."],
                ["Workflow continuity", "Saved report history. Reopen, export, or revisit later."],
                ["Investor subscriptions", "Lite, Pro, and Plus tiers for casual to heavier-volume workflows."],
            ]}
            quote="“Every property looks the same on a spreadsheet. Buyer's Leverage shows me what's actually behind the deal.”"
            cta="/pricing#investor-plans"
            ctaLabel="See investor plans"
            sideCard={investorHero}
            afterMain={<InvestorPreviewTrio />}
        />
    );
}
